/**
 * usePricingPreview Hook
 *
 * Live pricing preview hook for PricingCalculator component.
 * Calculates pricing dynamically as user adjusts sliders.
 *
 * - Debounces slider inputs (300ms) to avoid excessive API calls
 * - Uses keepPreviousData to always show the last valid price (never 0€)
 *
 * Phase 3: Frontend Integration
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { previewPricing } from '@/lib/api/subscription-pricing'
import type { PricingPreviewRequest, PricingPreviewResponse } from '@/lib/api/subscription-pricing'
import { useDebounce } from '@/hooks/useDebounce'

const DEBOUNCE_MS = 300

export interface UsePricingPreviewParams {
  tierCode: 'starter' | 'pro' | 'enterprise'
  pricingModel: 'credit' | 'dimension'
  desiredCreditsMonthly?: number
  selectedProducts?: number
  selectedChannels?: number
  enabled?: boolean
}

export function usePricingPreview({
  tierCode,
  pricingModel,
  desiredCreditsMonthly,
  selectedProducts,
  selectedChannels,
  enabled = true,
}: UsePricingPreviewParams) {
  const debouncedCredits = useDebounce(desiredCreditsMonthly, DEBOUNCE_MS)
  const debouncedProducts = useDebounce(selectedProducts, DEBOUNCE_MS)
  const debouncedChannels = useDebounce(selectedChannels, DEBOUNCE_MS)

  const request: PricingPreviewRequest = {
    tier_code: tierCode,
    pricing_model: pricingModel,
    desired_credits_monthly: debouncedCredits,
    selected_products: debouncedProducts,
    selected_channels: debouncedChannels,
  }

  return useQuery<PricingPreviewResponse, Error>({
    queryKey: [
      'pricing-preview',
      tierCode,
      pricingModel,
      debouncedCredits,
      debouncedProducts,
      debouncedChannels,
    ],
    queryFn: () => previewPricing(request),
    enabled: enabled && (pricingModel === 'credit' ? !!debouncedCredits : !!debouncedProducts),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
    retry: 2,
  })
}
